import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  CircleCheck,
  CircleUserRound,
  Copy,
  Lock,
  Unlock,
  CircleSlashIcon,
  Download,
} from "lucide-react";

interface FeedbackHeaderProps {
  status: "proposed" | "saved" | "published" | undefined;
  isModified: boolean;
  isUnlocked: boolean;
  onSave: () => void | Promise<void>;
  onUnlock: () => void;
  onDownload: () => void;
  onCopyFeedback: () => void;
  isSaving: boolean;
  currentContent: string;
  feedbackExportEnabled: boolean;
  isPublishingCurrentDocument?: boolean;
}

export const FeedbackHeader = ({
  status,
  isModified,
  isUnlocked,
  onSave,
  onUnlock,
  onDownload,
  onCopyFeedback,
  isSaving,
  currentContent,
  feedbackExportEnabled,
  isPublishingCurrentDocument = false,
}: FeedbackHeaderProps) => {
  if (!status) return null;

  const isPublished = status === "published";
  const showSaveButton = !(isPublished && !isUnlocked);

  const getStatusIcon = () => {
    if (isPublished) {
      if (isUnlocked) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onUnlock}
                className="cursor-pointer hover:opacity-80 transition-opacity"
                aria-label="Lock to prevent changes"
              >
                <Unlock className="h-4 w-4 text-muted-foreground" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Lock to prevent changes</p>
            </TooltipContent>
          </Tooltip>
        );
      }
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onUnlock}
              className="cursor-pointer hover:opacity-80 transition-opacity"
              aria-label="Unlock to make changes"
            >
              <Lock className="h-4 w-4 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Unlock to make changes</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    if (status === "saved") {
      return (
        <CircleUserRound
          className={`h-4 w-4 ${isModified ? "text-brand" : "text-blue-600"}`}
        />
      );
    }

    if (isModified) {
      return (
        <CircleSlashIcon
          className="h-4 w-4 text-brand"
        />
      );  
    }

    return (
      <CircleCheck
        className="h-4 w-4 text-green-600"
      />
    );
  };

  const getLabel = () => {
    switch (status) {
      case "proposed":
        return "Generated";
      case "saved":
        return "User";
      case "published":
        return "Published";
      default:
        return "";
    }
  };

  const getDescription = () => {
    if (status === "proposed") {
      return isModified ? "Feedback edited" : "All annotations";
    }

    if (status === "saved") {
      return isModified ? "Feedback modified" : "Manual edits preserved";
    }

    if (status === "published") {
      if (!isUnlocked) {
        return "You can no longer make changes";
      }
      return isModified ? "Feedback modified" : "No changes detected";
    }

    return "";
  };

  return (
    <div className="mb-4 px-2">
      <div className="flex items-center gap-2 mb-3">
        {getStatusIcon()}
        <span className="text-sm" data-test-role="feedback-status">{getLabel()}</span>
        <span className="text-muted-foreground text-sm">
          · {getDescription()}
        </span>

        <div className="flex-1" />

        {showSaveButton && (
          <Button
            variant="outline"
            size="sm"
            onClick={onSave}
            disabled={isSaving || !isModified}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        )}

        {isPublished && feedbackExportEnabled && (
          <Button 
            variant="ghost" 
            onClick={onDownload}
            disabled={isPublishingCurrentDocument}
          >
            <Download className="h-4 w-4" /> Download
          </Button>
        )}
        {currentContent && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onCopyFeedback}
            className="flex items-center gap-2"
          >
            <Copy className="h-4 w-4" />
            Copy
          </Button>
        )}
      </div>
      <Separator />
    </div>
  );
};
